import type { ChangeEvent } from "react";
import type { BancoDoCatalogo } from "@/api/contrato";
import { Campo } from "@/componentes/Campo";
import { Selecao } from "@/componentes/Selecao";
import { banco as nomeCurtoDoBanco } from "@/formato";
import { bancoDoCatalogo, gruposDeBancos } from "./bancos";
import {
  DICA_DA_JANELA,
  TIPOS_DE_CONTA,
  dicaDoBanco,
  dicaDoCatalogo,
  exigeCodigoBeneficiario,
  regrasDoBanco,
  type CampoDaConta,
  type ErrosDaConta,
  type ValoresDaConta,
} from "./conta";

/*
 * Os campos da conta, compartilhados por criação e edição, montados pelo catálogo.
 *
 * O que muda de banco para banco é regra, e regra vai na `Dica` do rótulo: a orientação
 * vem do provider, e o provider avisa que é dica. Nada é pré-preenchido, porque o código
 * do beneficiário "comumente" ser a conta é exatamente o que pode não valer.
 *
 * Antes de escolher o banco só existe o banco: pedir agência sem saber quantos dígitos
 * ela tem, ou se ela tem DV, é o que fazia a tela de antes levar `422`.
 */
const LINHA = "grid grid-cols-[minmax(0,1fr)_7rem] gap-3";

export function FormularioDeConta({
  valores,
  erros,
  aoMudar,
  bancos,
  carregandoBancos,
  falhaDosBancos,
  bancoFixo = false,
  travado = false,
}: {
  valores: ValoresDaConta;
  erros: ErrosDaConta;
  aoMudar: (campo: CampoDaConta, valor: string) => void;
  bancos: readonly BancoDoCatalogo[];
  carregandoBancos: boolean;
  /** A leitura do catálogo falhou: sem ele, não há o que escolher. */
  falhaDosBancos: string | undefined;
  /** Na edição o banco não muda: conta de outro banco é outra conta. */
  bancoFixo?: boolean;
  /** A janela fechou: todo campo daqui é bancário, e fica só para leitura. */
  travado?: boolean;
}) {
  const escolhido = bancoDoCatalogo(bancos, valores.banco);
  const regras = regrasDoBanco(escolhido);

  const dicaDo = (orientacao: string | undefined) =>
    travado ? DICA_DA_JANELA : dicaDoCatalogo(orientacao);

  // `aria-required`, e não `required`: o balão do navegador chegaria antes do erro no
  // campo, e pararia no primeiro em vez de mostrar todos.
  const ligar = (campo: CampoDaConta, obrigatorio: boolean) => ({
    value: valores[campo],
    onChange: (evento: ChangeEvent<HTMLInputElement>) => aoMudar(campo, evento.target.value),
    erro: erros[campo],
    disabled: travado,
    autoComplete: "off",
    className: "dados",
    "aria-required": obrigatorio,
  });

  return (
    <>
      {bancoFixo ? (
        <Campo
          rotulo="Banco"
          value={
            escolhido === undefined
              ? nomeCurtoDoBanco(valores.banco)
              : `${escolhido.codigo} ${escolhido.nome}`
          }
          readOnly
          dica={dicaDoBanco(escolhido)}
          erro={falhaDosBancos}
        />
      ) : (
        <Selecao
          rotulo="Banco"
          opcoes={[["", carregandoBancos ? "Carregando" : "Selecione"]]}
          grupos={gruposDeBancos(bancos)}
          value={valores.banco}
          onChange={(evento) => aoMudar("banco", evento.target.value)}
          dica={dicaDoBanco(escolhido)}
          erro={erros.banco ?? falhaDosBancos}
          // Só a falha desabilita: desabilitado enquanto carrega, perderia o `autoFocus`.
          disabled={falhaDosBancos !== undefined}
          autoFocus
        />
      )}

      {(bancoFixo || escolhido !== undefined) && (
        <>
          <div className={LINHA}>
            <Campo
              rotulo={regras.rotuloDaAgencia}
              inputMode="numeric"
              autoFocus={bancoFixo && !travado}
              dica={dicaDo(escolhido?.orientacao_agencia)}
              {...ligar("agencia", true)}
            />
            {regras.dvDaAgencia !== "nao_usado" && (
              <Campo
                rotulo="DV da agência"
                maxLength={2}
                dica={dicaDo(undefined)}
                {...ligar("agencia_dv", regras.dvDaAgencia === "obrigatorio")}
              />
            )}
          </div>

          <div className={LINHA}>
            <Campo
              rotulo="Conta"
              inputMode="numeric"
              dica={dicaDo(escolhido?.orientacao_conta)}
              {...ligar("conta", true)}
            />
            {regras.dvDaConta !== "nao_usado" && (
              <Campo
                rotulo="DV da conta"
                maxLength={2}
                dica={dicaDo(undefined)}
                {...ligar("conta_dv", regras.dvDaConta === "obrigatorio")}
              />
            )}
          </div>

          <div className={LINHA}>
            <Campo
              rotulo="Código do beneficiário"
              inputMode="numeric"
              dica={dicaDo(escolhido?.orientacao_codigo_beneficiario)}
              {...ligar("codigo_beneficiario", exigeCodigoBeneficiario(regras))}
            />
            <Selecao
              rotulo="Tipo"
              opcoes={TIPOS_DE_CONTA}
              value={valores.tipo}
              onChange={(evento) => aoMudar("tipo", evento.target.value)}
              dica={dicaDo(undefined)}
              erro={erros.tipo}
              disabled={travado}
            />
          </div>

          {regras.aceitaBancoCorrespondente && (
            <div className={LINHA}>
              <Campo
                rotulo="Banco correspondente"
                inputMode="numeric"
                dica={dicaDo("Código de 3 dígitos. Em branco na conta comum.")}
                {...ligar("banco_correspondente", false)}
              />
            </div>
          )}
        </>
      )}
    </>
  );
}
